import RowLabel from "@/main/components/common/rowLabel/rowLabel";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputAmountControlled from "@/main/components/shared/inputText/inputAmountControlled";
import KOGImage from "@/main/components/shared/kogImage/kogImage";
import WrapperQuoteProcurementPartInfo from "@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo";
import TooltipComponent from "@/main/components/shared/tooltip/tooltipComponent";
import { KeyValueDefault } from "@/main/models/common";
import { GetItemsCancelledBySupplierResponse } from "@/main/models/response";
import { ReassignBodyshopPartsCancelledForm } from "@/main/screens/order/cancelledParts/components/modalReassignBodyshopPartsCancelled/modalReassignBodyshopPartsCancelledUtils";
import { PartTypeEnum } from "@/main/services/enum/PartTypeEnum";
import { isNullUndefined } from "@/main/services/reducerUtils";
import { getTranslateByKey } from "@/main/services/utils";
import { formatCurrency } from "@/main/services/utilsNumber";
import { useBrandReducer } from "@/main/store/brand";
import { useFormContext, useWatch } from "react-hook-form";
import * as S from "./styles";
import { memo } from "react";

interface IRowItemReassignBodyshopCancelled {
	partData: GetItemsCancelledBySupplierResponse;
	index: number;
	partTypeOptions: KeyValueDefault[];
}

function RowItemReassignBodyshopCancelled({
	index,
	partData,
	partTypeOptions,
}: Readonly<IRowItemReassignBodyshopCancelled>) {
	const { control, setValue } = useFormContext<ReassignBodyshopPartsCancelledForm>();

	const {
		brand: { data: brands, isLoading: isLoadingBrands },
	} = useBrandReducer();

	const watcherPartType = useWatch({
		control,
		name: `parts.${index}.partType`,
	});

	const showBrandDrop = !isNullUndefined(watcherPartType) && Number(watcherPartType.default) !== PartTypeEnum.OEM;

	return (
		<S.RowPart>
			<S.Container>
				<KOGImage src={partData.quoteProcurementPartInfo?.image} alt="" />

				<S.AreaTitlePart>
					<TooltipComponent
						interactive
						placement="top-start"
						title={
							<div>
								<RowLabel label={getTranslateByKey("information.claimNumber")} content={partData.claimNumber} />
								<RowLabel label={getTranslateByKey("information.order")} content={partData.procurementCode} />
								<RowLabel label={getTranslateByKey("information.quote")} content={partData.quoteCode} />
								<RowLabel label={getTranslateByKey("information.supplier")} content={partData.supplierName} />
								<RowLabel label={getTranslateByKey("information.vehicle")} content={partData.vehicleFullName} />
							</div>
						}
					>
						<div style={{ minWidth: 0, overflow: "hidden", width: "100%" }}>
							<WrapperQuoteProcurementPartInfo data={partData.quoteProcurementPartInfo} renderType="column" />
						</div>
					</TooltipComponent>
					<RowLabel
						content={formatCurrency(partData.currentPrice)}
						label={getTranslateByKey("information.currentPrice")}
					/>
				</S.AreaTitlePart>
			</S.Container>

			<S.AreaInputs $numColumns={showBrandDrop ? 3 : 2}>
				<KogInputAmountControlled
					id={`id-cancelledPartsReassignBodyshop-input-price-${index}`}
					name={`parts.${index}.price`}
					control={control}
					typeInput="filled"
					required
					label={getTranslateByKey("information.price")}
				/>

				<KogAutoCompleteControlled
					id={`id-cancelledPartsReassignBodyshop-select-partType-${index}`}
					nameCustom={`parts.${index}.partType`}
					label={getTranslateByKey("information.partTypes")}
					required
					rulesCustom={{ required: true }}
					control={control}
					isDefaultValueUnique
					setValueCustom={setValue}
					options={partTypeOptions}
					onChangeCustom={(option) => {
						if (option && Number((option as KeyValueDefault).default) === PartTypeEnum.OEM) {
							setValue(`parts.${index}.brand`, null);
						}
					}}
				/>
				{showBrandDrop && (
					<KogAutoCompleteControlled
						id={`id-cancelledPartsReassignBodyshop-select-brand-${index}`}
						label={getTranslateByKey("information.brand")}
						nameCustom={`parts.${index}.brand`}
						control={control}
						isDefaultValueUnique
						setValueCustom={setValue}
						options={brands}
						isLoading={isLoadingBrands}
						onChangeCustom={() => {}}
					/>
				)}
			</S.AreaInputs>
		</S.RowPart>
	);
}

export default memo(RowItemReassignBodyshopCancelled);
